import React, { useState, useEffect } from 'react'
import { T } from './ui'
import { getNFLProof } from './api'

// PUBLIC track-record page — reachable at /proof (bypasses the age + access gates,
// like the legal pages). The shareable case-for-the-product a cold visitor sees
// before the paywall: settled record, CLV, tier breakdown, weekly trend and
// calibration — nothing but honest, graded results. No picks, no stakes, no advice.
// Reads /nfl/proof (ungated). When nothing's settled it shows an honest "building"
// state, never a fabricated record.

const SEASON = Number(import.meta.env.VITE_NFL_SEASON) || 2026

const PROP_LABEL = {
  receptions: 'Receptions', anytime_td: 'Anytime TD', pass_tds: 'Pass TDs',
  rec_yards: 'Rec Yds', rush_yards: 'Rush Yds', pass_yards: 'Pass Yds',
}
const rateColor = r => r == null ? T.muted : r >= 60 ? T.green : r >= 52 ? T.mint : r >= 48 ? T.amber : T.red

function Stat({ big, label, sub, color }) {
  return (
    <div style={{ flex: 1, textAlign: 'center', padding: '4px 2px' }}>
      <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 30, lineHeight: 1, color: color || T.text }}>{big}</div>
      <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.06em', marginTop: 6, textTransform: 'uppercase' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: T.muted2, marginTop: 3 }}>{sub}</div>}
    </div>
  )
}

function Card({ children, accent }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${accent || T.border}`, borderRadius: 14,
      padding: 16, marginBottom: 12 }}>{children}</div>
  )
}

function SectionTitle({ children, hint }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: T.text, letterSpacing: '0.01em' }}>{children}</div>
      {hint && <div style={{ fontSize: 11, color: T.muted, marginTop: 2, lineHeight: 1.4 }}>{hint}</div>}
    </div>
  )
}

// One horizontal bar (hit rate) with a 50% reference tick.
function RateBar({ label, right, rate, plays }) {
  const w = Math.max(2, Math.min(100, rate == null ? 0 : rate))
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{label}</span>
        <span style={{ fontSize: 11, color: T.muted }}>
          {right} · <span style={{ fontFamily: T.mono, fontWeight: 800, color: rateColor(rate) }}>{rate == null ? '—' : `${rate}%`}</span>
        </span>
      </div>
      <div style={{ position: 'relative', height: 8, background: T.card2, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, width: `${w}%`, background: rateColor(rate), borderRadius: 6, transition: 'width .4s' }} />
        {/* break-even reference at 50% (a coin flip) */}
        <div style={{ position: 'absolute', top: -1, bottom: -1, left: '50%', width: 1, background: T.border2 }} />
      </div>
      <div style={{ fontSize: 10, color: T.faint, marginTop: 2 }}>{plays} settled</div>
    </div>
  )
}

export default function ProofPage() {
  const [d, setD] = useState(null)
  useEffect(() => { let a = true; getNFLProof(SEASON).then(r => { if (a) setD(r) }); return () => { a = false } }, [])

  // Absolute path — a relative '?sport=nfl' would resolve to /proof?sport=nfl, and
  // App.jsx renders this page for any /proof path, trapping the visitor here.
  const enter = () => { window.location.href = '/?sport=nfl' }

  const ov = d?.overall, rz = d?.realized, tp = d?.top20
  const settled = d?.settled || 0
  const hasRecord = settled > 0
  const rf = d?.recent_form
  const weeks = (d?.weeks || []).filter(w => w.plays)
  const tiers = (d?.tiers || []).filter(t => t.plays)
  const byProp = d?.by_prop || []
  const buckets = (d?.calibration?.buckets || []).filter(b => b.n >= 3)

  return (
    <div style={{ background: T.bg, minHeight: '100vh', color: T.text,
      fontFamily: "-apple-system,'SF Pro Display',Arial,sans-serif" }}>
      <div style={{ maxWidth: 460, margin: '0 auto', padding: '0 16px 40px' }}>

        {/* Header */}
        <div style={{ paddingTop: 22, paddingBottom: 8, textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, justifyContent: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 900, fontSize: 26, letterSpacing: '-0.04em' }}>
              <span style={{ color: T.text }}>LINE</span><span style={{ color: T.green }}>BRKR</span>
            </div>
            <span style={{ fontWeight: 800, fontSize: 11, color: '#04140D', background: T.green,
              borderRadius: 5, padding: '2px 6px', letterSpacing: '0.06em' }}>NFL</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.2 }}>The track record</div>
          <div style={{ fontSize: 13, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
            Every number below is a <b style={{ color: T.muted2 }}>settled, graded result</b> — not a projection.
            This is how the model's surfaced plays actually did, {SEASON} season to date.
          </div>
        </div>

        {!d ? (
          <div style={{ textAlign: 'center', color: T.muted, padding: '48px 0', fontSize: 13 }}>Loading the record…</div>
        ) : !hasRecord ? (
          /* Honest pre-season / nothing-settled state — never a fake record */
          <Card accent={T.greenMid}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>The record starts here 🏈</div>
            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
              No plays have settled yet for {SEASON}. As each game finalizes, every surfaced play is graded
              and the hit rate, closing-line value and calibration all post right here — automatically, win or lose.
            </div>
          </Card>
        ) : (
          <>
            {/* Headline record */}
            <Card accent={T.greenMid}>
              <div style={{ display: 'flex', alignItems: 'stretch' }}>
                <Stat big={ov?.hit_rate != null ? `${ov.hit_rate}%` : '—'} label="Hit rate"
                  sub={`${ov?.wins || 0}–${ov?.losses || 0}`} color={rateColor(ov?.hit_rate)} />
                <div style={{ width: 1, background: T.border }} />
                <Stat big={settled} label="Plays settled" sub="over-line, surfaced" />
              </div>
              <div style={{ fontSize: 10.5, color: T.faint, marginTop: 10, textAlign: 'center', lineHeight: 1.4 }}>
                Over-line record only — the exact side the board surfaced. Under-leans the board never showed are excluded, so nothing inflates this number.
              </div>
            </Card>

            {/* Realized / CLV — the sharpest proof */}
            {rz && (rz.plays > 0 || rz.clv_n > 0) && (
              <Card>
                <SectionTitle hint="Graded against the actual posted line — and whether our number beat the closing line (the cleanest proof of edge there is).">
                  Beat the book
                </SectionTitle>
                <div style={{ display: 'flex', alignItems: 'stretch', marginTop: 6 }}>
                  <Stat big={rz.hit_rate != null ? `${rz.hit_rate}%` : '—'} label="Book-line" sub={`${rz.wins}–${rz.losses}`} color={rateColor(rz.hit_rate)} />
                  <div style={{ width: 1, background: T.border }} />
                  <Stat big={rz.mean_clv != null ? `${rz.mean_clv > 0 ? '+' : ''}${rz.mean_clv}` : '—'} label="Mean CLV" color={rz.mean_clv > 0 ? T.green : rz.mean_clv < 0 ? T.red : T.muted} />
                  <div style={{ width: 1, background: T.border }} />
                  <Stat big={rz.clv_positive_rate != null ? `${rz.clv_positive_rate}%` : '—'} label="Beat close" sub={rz.clv_n ? `${rz.clv_n} priced` : ''} color={rateColor(rz.clv_positive_rate)} />
                </div>
              </Card>
            )}

            {/* Recent form + top-20 */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              {rf && (
                <div style={{ flex: 1, background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 12px', textAlign: 'center' }}>
                  <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 24, color: rateColor(rf.hit_rate) }}>{rf.hit_rate != null ? `${rf.hit_rate}%` : '—'}</div>
                  <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.05em', marginTop: 5, textTransform: 'uppercase' }}>Last {rf.weeks} wk{rf.weeks === 1 ? '' : 's'}</div>
                  <div style={{ fontSize: 11, color: T.muted2, marginTop: 2 }}>{rf.wins}–{rf.losses}</div>
                </div>
              )}
              {tp && tp.plays > 0 && (
                <div style={{ flex: 1, background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 12px', textAlign: 'center' }}>
                  <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 24, color: rateColor(tp.hit_rate) }}>{tp.hit_rate != null ? `${tp.hit_rate}%` : '—'}</div>
                  <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.05em', marginTop: 5, textTransform: 'uppercase' }}>Top 20 plays</div>
                  <div style={{ fontSize: 11, color: T.muted2, marginTop: 2 }}>highest conviction</div>
                </div>
              )}
            </div>

            {/* Do the high-conviction plays hit — tier breakdown */}
            {tiers.length > 0 && (
              <Card>
                <SectionTitle hint="The higher the conviction tier, the higher the hit rate should run — proof the ratings mean something.">
                  Do the top-rated plays hit?
                </SectionTitle>
                <div style={{ marginTop: 8 }}>
                  {tiers.map(t => (
                    <RateBar key={t.tier} label={t.tier} right={`${t.wins}/${t.plays}`} rate={t.hit_rate} plays={t.plays} />
                  ))}
                </div>
              </Card>
            )}

            {/* Week by week */}
            {weeks.length > 0 && (
              <Card>
                <SectionTitle hint="Over-line hit rate per settled week — the trend, oldest to newest.">Week by week</SectionTitle>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 92, marginTop: 10 }}>
                  {weeks.map(w => {
                    const h = Math.max(6, Math.min(100, w.hit_rate || 0))
                    return (
                      <div key={w.week} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 10, fontFamily: T.mono, fontWeight: 800, color: rateColor(w.hit_rate) }}>{w.hit_rate != null ? w.hit_rate : '—'}</span>
                        <div style={{ width: '100%', height: `${h}%`, minHeight: 4, background: rateColor(w.hit_rate), borderRadius: '4px 4px 0 0', opacity: 0.9 }} />
                        <span style={{ fontSize: 10, color: T.muted }}>W{w.week}</span>
                      </div>
                    )
                  })}
                </div>
                <div style={{ fontSize: 10, color: T.faint, marginTop: 8, textAlign: 'center' }}>Bar = over-line hit rate · dashed midline is a coin flip (50%)</div>
              </Card>
            )}

            {/* Calibration — honesty of the probabilities */}
            {buckets.length > 0 && (
              <Card>
                <SectionTitle hint="When the model says 70%, does it hit ~70%? Predicted vs what actually happened.">
                  Are the probabilities honest?
                </SectionTitle>
                <div style={{ marginTop: 8 }}>
                  {buckets.map(b => (
                    <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ fontSize: 11, color: T.muted, width: 58, flexShrink: 0, fontFamily: T.mono }}>{b.label}</span>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, color: T.muted2 }}>said <b style={{ color: T.text }}>{b.pred_pct}%</b></span>
                        <span style={{ color: T.faint }}>→</span>
                        <span style={{ fontSize: 11, color: T.muted2 }}>hit <b style={{ color: rateColor(b.obs_pct) }}>{b.obs_pct}%</b></span>
                      </div>
                      <span style={{ fontSize: 10, color: T.faint, flexShrink: 0 }}>n={b.n}</span>
                    </div>
                  ))}
                </div>
                {d?.calibration?.brier != null && (
                  <div style={{ fontSize: 10.5, color: T.faint, marginTop: 6, textAlign: 'center' }}>Brier score {d.calibration.brier} · lower is sharper</div>
                )}
              </Card>
            )}

            {/* By market */}
            {byProp.length > 0 && (
              <Card>
                <SectionTitle hint="Record by prop market, biggest sample first.">By market</SectionTitle>
                <div style={{ marginTop: 8 }}>
                  {byProp.map(p => (
                    <RateBar key={p.prop} label={PROP_LABEL[p.prop] || p.prop} right={`${p.wins}/${p.plays}`} rate={p.hit_rate} plays={p.plays} />
                  ))}
                </div>
              </Card>
            )}
          </>
        )}

        {/* CTA */}
        <div style={{ marginTop: 8 }}>
          <button onClick={enter} style={{ width: '100%', padding: '15px 0', fontSize: 15, fontWeight: 900,
            background: T.green, color: '#04140D', border: 'none', borderRadius: 12, cursor: 'pointer', letterSpacing: '0.01em' }}>
            See this week's board →
          </button>
          <div style={{ fontSize: 11, color: T.muted, textAlign: 'center', marginTop: 10, lineHeight: 1.5 }}>
            Player props · hit % + edge · matchup grades · red-zone & alignment cheatsheet.
          </div>
        </div>

        {/* Responsible-gaming footer */}
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: `1px solid ${T.border}`, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.6 }}>
            <b style={{ color: T.text }}>21+</b> · Research &amp; analytics for entertainment only — not betting advice, not a sportsbook, and it does not guarantee outcomes.
            <br />Bet responsibly. Problem gambling? Call or text <b style={{ color: T.text }}>1-800-GAMBLER</b>.
          </div>
          <div style={{ fontSize: 11, color: T.faint, marginTop: 10 }}>
            <a href="/terms" style={{ color: T.muted, textDecoration: 'none' }}>Terms</a> ·{' '}
            <a href="/privacy" style={{ color: T.muted, textDecoration: 'none' }}>Privacy</a> ·{' '}
            <a href="/disclaimer" style={{ color: T.muted, textDecoration: 'none' }}>Disclaimer</a>
          </div>
        </div>
      </div>
    </div>
  )
}
